/**
 * Browse-Action Template: Browse → Select → Action buttons
 *
 * This template handles the browse-and-act flow where users select items
 * and then choose an action (compare, quote, contact, etc.)
 *
 * User flow:
 * 1. Browse items with filters
 * 2. Select one or more items
 * 3. Action buttons become enabled
 * 4. Click action to emit event with selected items
 */

import { registerBlockRenderer, getBlockRendererContext, limitArray } from '../registry';
import { escapeHtml, createBlockWrapper, createErrorState, createEmptyState, disableButtonTemporarily } from '../utils';
import { inlineLoaderHTML } from '../../../core/loading/activity-loader';
import { iconChevronDown } from '../../icons';
import type { BrowseActionData } from './types';
import type { LiveDataItem, LiveSectionResponse } from '../../../core/api-client';

// =============================================================================
// BROWSE-ACTION TEMPLATE
// =============================================================================

registerBlockRenderer('browse-action', (data: unknown, container: HTMLElement, onAction?: (value: string) => void) => {
  const templateData = data as BrowseActionData;
  const ctx = getBlockRendererContext();

  if (!templateData.section) {
    console.warn('[browse-action] Missing required "section" field');
    const wrapper = createBlockWrapper('browse-action');
    wrapper.innerHTML = createErrorState('This content is not available');
    container.appendChild(wrapper);
    return;
  }

  // Create main wrapper
  const wrapper = createBlockWrapper('browse-action');
  wrapper.classList.add('pms-template-browse-action');

  // Internal state
  const state = {
    selectedItems: new Map<string, LiveDataItem>(),
    items: [] as LiveDataItem[],
    filters: [] as { id: string; label: string; options: string[] }[],
    activeFilters: new Map<string, string>(),
  };

  // Loading state
  wrapper.innerHTML = `
    <div class="pms-template-loading">
      ${inlineLoaderHTML('Loading...')}
    </div>
  `;
  container.appendChild(wrapper);

  // Fetch data and render
  loadAndRender();

  async function loadAndRender(): Promise<void> {
    if (!ctx.apiClient || !ctx.workspaceId) {
      wrapper.innerHTML = createErrorState('This content is not available right now');
      return;
    }

    try {
      const apiClient = ctx.apiClient as {
        getLiveSectionData: (workspaceId: string, sectionId: string, filters: Record<string, string>, limit: number) => Promise<LiveSectionResponse>
      };

      const response = await apiClient.getLiveSectionData(
        ctx.workspaceId,
        templateData.section,
        templateData.initialFilters || {},
        50
      );

      state.items = limitArray(response.items, 50);
      state.filters = limitArray(response.filters || [], 10).map(f => ({
        id: f.id,
        label: f.label,
        options: limitArray(f.options.map(o => o.value), 50)
      }));

      // Initialize active filters
      if (templateData.initialFilters) {
        for (const [key, value] of Object.entries(templateData.initialFilters)) {
          state.activeFilters.set(key, value);
        }
      }

      wrapper.innerHTML = '';
      renderTemplate();

    } catch (error) {
      console.error('[browse-action] Failed to load data:', error);
      wrapper.innerHTML = createErrorState('This content could not be loaded right now');
    }
  }

  function renderTemplate(): void {
    const title = templateData.title || 'Browse Items';

    // Header
    const header = document.createElement('div');
    header.className = 'pms-template-header';
    header.innerHTML = `
      <h3 class="pms-template-title">${escapeHtml(title)}</h3>
      <span class="pms-template-count">${state.items.length} items</span>
      <span class="pms-template-selection" style="display: none;"></span>
    `;
    wrapper.appendChild(header);

    // Filters
    if (state.filters.length > 0) {
      const filtersEl = renderFilters();
      wrapper.appendChild(filtersEl);
    }

    // Browse grid
    const grid = document.createElement('div');
    grid.className = 'pms-template-grid';
    renderItems(grid);
    wrapper.appendChild(grid);

    // Action buttons (always visible, but disabled until selection)
    const actionsEl = renderActions();
    wrapper.appendChild(actionsEl);

    updateSelectionUI();
  }

  function renderFilters(): HTMLElement {
    const filtersWrapper = document.createElement('div');
    filtersWrapper.className = 'pms-browse-filters';

    for (const filter of state.filters) {
      const filterGroup = document.createElement('div');
      filterGroup.className = 'pms-browse-filter-item';

      const label = document.createElement('label');
      label.className = 'pms-browse-filter-label';
      label.textContent = filter.label;
      filterGroup.appendChild(label);

      const selectWrapper = document.createElement('div');
      selectWrapper.className = 'pms-browse-select-wrapper';

      const select = document.createElement('select');
      select.className = 'pms-browse-select';
      select.dataset['filterId'] = filter.id;

      const allOption = document.createElement('option');
      allOption.value = '';
      allOption.textContent = 'All';
      select.appendChild(allOption);

      for (const option of filter.options) {
        const opt = document.createElement('option');
        opt.value = option;
        opt.textContent = option;
        opt.selected = state.activeFilters.get(filter.id)?.toLowerCase() === option.toLowerCase();
        select.appendChild(opt);
      }

      select.addEventListener('change', () => {
        state.activeFilters.set(filter.id, select.value);
        applyFilters();
      });

      const chevron = document.createElement('span');
      chevron.className = 'pms-browse-select-chevron';
      chevron.innerHTML = iconChevronDown();

      selectWrapper.appendChild(select);
      selectWrapper.appendChild(chevron);
      filterGroup.appendChild(selectWrapper);
      filtersWrapper.appendChild(filterGroup);
    }

    return filtersWrapper;
  }

  function renderItems(grid: HTMLElement): void {
    grid.innerHTML = '';

    if (state.items.length === 0) {
      grid.innerHTML = createEmptyState('No items found', { compact: true });
      return;
    }

    for (const item of state.items) {
      const card = renderItemCard(item);
      grid.appendChild(card);
    }
  }

  function renderItemCard(item: LiveDataItem): HTMLElement {
    const card = document.createElement('div');
    card.className = 'pms-template-card';
    card.dataset['itemId'] = item.id;
    card.dataset['tags'] = [item.category, item.subcategory].filter(Boolean).join(',').toLowerCase();

    // Store filter metadata
    if (item.metadata) {
      for (const [key, value] of Object.entries(item.metadata)) {
        if (value !== undefined && value !== null) {
          card.dataset['filter' + key.charAt(0).toUpperCase() + key.slice(1).toLowerCase()] = String(value).toLowerCase();
        }
      }
    }

    // Selection checkbox
    const checkbox = document.createElement('div');
    checkbox.className = 'pms-template-card-checkbox';
    checkbox.innerHTML = `
      <label class="pms-browse-checkbox-wrapper">
        <input type="checkbox" class="pms-browse-checkbox" />
        <span class="pms-browse-checkbox-mark"></span>
      </label>
    `;
    card.appendChild(checkbox);

    // Image
    if (item.imageUrl) {
      const img = document.createElement('img');
      img.className = 'pms-template-card-image';
      img.src = item.imageUrl;
      img.alt = item.title;
      img.loading = 'lazy';
      img.onerror = () => img.style.display = 'none';
      card.appendChild(img);
    }

    // Content
    const content = document.createElement('div');
    content.className = 'pms-template-card-content';

    if (item.badge) {
      content.innerHTML += `<span class="pms-template-card-badge">${escapeHtml(item.badge)}</span>`;
    }
    content.innerHTML += `<h4 class="pms-template-card-title">${escapeHtml(item.title)}</h4>`;
    if (item.description) {
      content.innerHTML += `<p class="pms-template-card-desc">${escapeHtml(item.description)}</p>`;
    }
    if (item.price) {
      content.innerHTML += `<span class="pms-template-card-price">${escapeHtml(item.price)}</span>`;
    }

    card.appendChild(content);

    // Click handler
    const input = checkbox.querySelector('input') as HTMLInputElement;
    card.addEventListener('click', (e) => {
      if ((e.target as HTMLElement).closest('a')) return;

      const isMulti = templateData.selectMode !== 'single';

      if (!isMulti) {
        // Single select - clear others
        state.selectedItems.clear();
        wrapper.querySelectorAll('.pms-template-card').forEach(c => {
          c.classList.remove('pms-template-card-selected');
          (c.querySelector('input') as HTMLInputElement).checked = false;
        });
      }

      if (state.selectedItems.has(item.id)) {
        state.selectedItems.delete(item.id);
        card.classList.remove('pms-template-card-selected');
        input.checked = false;
      } else {
        state.selectedItems.set(item.id, item);
        card.classList.add('pms-template-card-selected');
        input.checked = true;
      }

      updateSelectionUI();
    });

    return card;
  }

  function renderActions(): HTMLElement {
    const actionsWrapper = document.createElement('div');
    actionsWrapper.className = 'pms-template-actions';

    for (const action of templateData.actions || []) {
      const btn = document.createElement('button');
      btn.className = 'pms-template-action-btn';
      if (action.variant === 'primary') {
        btn.classList.add('pms-template-action-primary');
      }
      btn.textContent = action.label;
      btn.dataset['action'] = action.action;
      btn.dataset['minSelect'] = String(action.minSelect ?? 1);
      btn.disabled = true;

      btn.addEventListener('click', () => {
        const selectedIds = Array.from(state.selectedItems.keys());

        // Format: action:section:ids=1,2,3
        const actionString = `${action.action}:${templateData.section}:ids=${selectedIds.join(',')}`;

        disableButtonTemporarily(btn, 2000);

        if (onAction) {
          onAction(actionString);
        }
      });

      actionsWrapper.appendChild(btn);
    }

    return actionsWrapper;
  }

  function updateSelectionUI(): void {
    const count = state.selectedItems.size;

    // Update selection count
    const selectionEl = wrapper.querySelector('.pms-template-selection') as HTMLElement;
    if (selectionEl) {
      if (count > 0) {
        selectionEl.textContent = `${count} selected`;
        selectionEl.style.display = '';
      } else {
        selectionEl.style.display = 'none';
      }
    }

    // Update action button states
    const buttons = wrapper.querySelectorAll('.pms-template-action-btn');
    buttons.forEach(btnEl => {
      const btn = btnEl as HTMLButtonElement;
      const minSelect = parseInt(btn.dataset['minSelect'] || '1', 10);
      btn.disabled = count < minSelect;
    });
  }

  function applyFilters(): void {
    const cards = wrapper.querySelectorAll('.pms-template-card');
    let visibleCount = 0;

    cards.forEach(cardEl => {
      const card = cardEl as HTMLElement;
      let visible = true;

      for (const [filterId, selectedValue] of state.activeFilters.entries()) {
        if (!selectedValue) continue;

        const camelKey = 'filter' + filterId.charAt(0).toUpperCase() + filterId.slice(1).toLowerCase();
        const cardValue = card.dataset[camelKey] || '';
        const cardTags = card.dataset['tags'] || '';

        const lowerVal = selectedValue.toLowerCase();
        if (!cardValue.includes(lowerVal) && !cardTags.includes(lowerVal)) {
          visible = false;
          break;
        }
      }

      card.style.display = visible ? '' : 'none';
      if (visible) visibleCount++;
    });

    // Update count
    const countEl = wrapper.querySelector('.pms-template-count');
    if (countEl) {
      countEl.textContent = `${visibleCount} items`;
    }
  }
});
